import { IColumn, ISelection, Selection, SelectionMode } from "@fluentui/react";
import { RootState } from "app/store";
import CustomDetailList from "components/controls/CustomDetailList";
import { DefaultGridConfig, UpdateColumnByConfig } from "components/controls/CustomDetailList/CustomDetailList";
import { ICustomDetailListConfiguration } from "components/controls/CustomDetailList/ICustomDetailListConfiguration";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import { filterPredicateColumnPropertyName, filterPredicateColumnPropertyString, onRenderColumnBenefitRef, onRenderColumnRef, sortPredicateColumnPropertyName, sortPredicateColumnPropertyString } from "components/controls/Grid";
import { AppLinkTo, DetailListExcelGenerationAsync } from "global/Application";
import { useApplication } from "global/Application/Context";
import { updateCustomDetailListConfiguration } from "global/Reducer/statusSlice";
import { ArrayToLookup } from "global/Utility/TSFunctions";
import * as FAM from "model/FlexibleAttributeModel";
import { IExtendedColumn } from "model/IExtendedColumn";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

interface IPlanGridProps {
    plans?: FAM.IPlan[];
    selection?: ISelection;
    disableRefNavigation?: boolean;
    selectionMode?: SelectionMode;
    defaultGridConfig?: ICustomDetailListConfiguration;
    onChange?: (plans: FAM.IPlan[]) => void;
}


interface IPlanGridItem extends IGridItem<FAM.IPlan> {
    name?: string;
    fundingType?: string;
    grandFathered?: string;
    feesAndPremiumRef?: FAM.IRef;
    planBenefits?: FAM.IBenefit[];
}

// const consolelog = console.log;

// const selectedKeys = {} as any;
// const staticSelection = new Selection({
//     onSelectionChanged: () => {
//         const items = staticSelection.getItems();
//         const selection = staticSelection.getSelection();
//         const selected = selectedKeys;

//         if (selection.length > 0) {

//             items.forEach(({ key }) => {
//                 delete selected[key as string];
//             });

//             selection.forEach(({ key }) => {
//                 selected[key as string] = true;
//             })
//         }

//         consolelog("onSelectionChanged", "items", items, "selection", selection, "selected", selected)
//     },

//     onItemsChanged: () => {
//         setImmediate(() => {
//             const items = staticSelection.getItems();
//             staticSelection.setChangeEvents(false, true);
//             for (const { key } of items) {
//                 if (selectedKeys[key as string])
//                     staticSelection.setKeySelected(key as string, true, false);
//                 else
//                     staticSelection.setKeySelected(key as string, false, false);
//             }

//             const selection = staticSelection.getSelection();
//             const selected = selectedKeys;

//             staticSelection.setChangeEvents(true, false)

//             consolelog("onItemsChanged", "items", items, "selection", selection, "selected", selected)

//         });
//     },
//     selectionMode: SelectionMode.multiple,
// });


const PlanGrid: React.FunctionComponent<IPlanGridProps> = (props) => {

    const history = useHistory();
    const application = useApplication();

    const state = useSelector((state: RootState) => state);

    const plans = props.plans ?? state.data.json.plans;

    const storageId = `plan.grid`;

    const dispatch = useDispatch();

    const scopeGridConfig = () => {
        const u = state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        // t.sort = [{ column: { key: 'attributeSetName' }, isSortedDescending: false }];

        return t;
    }

    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(props.defaultGridConfig ?? scopeGridConfig());
    const gridConfigRef = useRef(gridConfig);

    // check if it has changed since mounting
    if (gridConfig !== gridConfigRef.current) {
        // console.log("render:grid config updated", gridConfig);

        gridConfigRef.current = gridConfig;

        // snapshot for bind back
        dispatch(updateCustomDetailListConfiguration({ id: storageId, gridConfig: structuredClone(gridConfig) }));
    }


    const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
        setGridConfig(t => {

            const gridConfig: ICustomDetailListConfiguration = { ...t };

            gridConfig.sort = [{
                column: { key: column.key },
                isSortedDescending: !t?.sort?.[0]?.isSortedDescending ?? false,
            }];

            return gridConfig;
        });
    };

    const columns = useMemo(() => {
        const columns: IExtendedColumn[] = [];

        // Plan ------------------------------------------------------
        {
            const column: IExtendedColumn = {
                key: 'planRef',
                fieldName: 'planRef',
                name: 'Plan',
                minWidth: 400,
                disableNavigation: props.disableRefNavigation,
                onRender: onRenderColumnRef
            }

            column.onColumnClick = onColumnClick;
            column.sortPredicate = sortPredicateColumnPropertyName;
            column.filterPredicate = filterPredicateColumnPropertyName;
            UpdateColumnByConfig(column, gridConfig);

            columns.push(column);
        }


        // Grand Fathered ------------------------------------------------------
        {
            const column: IExtendedColumn = {
                key: 'grandFathered',
                fieldName: 'grandFathered',
                name: 'GF/NGF Type',
                minWidth: 150,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;


            columns.push(column);
        }

        // Funding Type ------------------------------------------------------
        {
            const column: IExtendedColumn = {
                key: 'fundingType',
                fieldName: 'fundingType',
                name: 'Funding Type',
                minWidth: 250,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;


            columns.push(column);
        }

        // Fees And Premium ------------------------------------------------------
        {
            const column: IExtendedColumn = {
                key: 'feesAndPremiumRef',
                fieldName: 'feesAndPremiumRef',
                name: 'Fees And Premium',
                minWidth: 250,
                onRender: onRenderColumnRef,
                disableNavigation: props.disableRefNavigation,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyName
            column.filterPredicate = filterPredicateColumnPropertyName;

            columns.push(column);
        }

        const cats = state.data.config.cache.bcbs_config_category_benefits

        // Benefits ------------------------------------------------------
        // order is defined by client:
        cats.forEach(cat => {
            const column: IExtendedColumn = {
                key: `${cat.bcbs_name}BenefitRef`,
                fieldName: `${cat.bcbs_name}BenefitRef`,
                name: `${cat.bcbs_name} Benefit`,
                minWidth: 200,
                disableNavigation: props.disableRefNavigation,
                onRender: onRenderColumnBenefitRef,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyName
            column.filterPredicate = filterPredicateColumnPropertyName;

            columns.push(column);
        });

        return columns;
    }, [gridConfig]);

    const [items, itemsPaged] = useMemo(() => {

        const items: IPlanGridItem[] = [];

        const benefits = state.data.json.benefits;
        const benefitsLookup = ArrayToLookup(benefits);

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columns?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columns?.find(w => w.key === q.column.key)),
        }

        const toExtract = (item: IPlanGridItem) => () => {
            const ePlan = item.data!;

            const row: Record<string, string | undefined> = {
                planRef: ePlan.name,
                feesAndPremiumRef: ePlan.feesAndPremiumsRef?.name,
                fundingType: ePlan.fundingType?.name,
                grandFathered: ePlan.grandFathered?.name,
            }

            item.planBenefits?.forEach(eachPlanBenefit => {
                const category = eachPlanBenefit.bcbs_name!;
                row[`${category}BenefitRef`] = eachPlanBenefit.name;
            })

            return row;
        }

        plans.forEach((ePlan, ix) => {

            const planBenefits = ePlan.benefitRefs.map(eB => benefitsLookup[eB.id]);

            // skip deactivated records/only show deactivated records if permitted at group config properties level
            if ((ePlan.deactivated ?? false) !== state.data.json.showInActives?.value)
                return;

            const item: IPlanGridItem = {
                _index: ix,
                key: ePlan.id!,
                data: ePlan,
                name: ePlan.name,
                planRef: { id: ePlan.id, name: ePlan.name },
                plan: ePlan,
                planBenefits: planBenefits,
                fundingType: ePlan.fundingType?.name,
                grandFathered: ePlan.grandFathered?.name,
                feesAndPremiumRef: ePlan.feesAndPremiumsRef
                    ? { id: ePlan.feesAndPremiumsRef.id, name: ePlan.feesAndPremiumsRef.name }
                    : undefined,

            };

            item.toExtract = toExtract(item);

            planBenefits.forEach(eachPlanBenefit => {
                const category = eachPlanBenefit.bcbs_name!;
                item[`${category}BenefitRef`] = { id: eachPlanBenefit.id, name: eachPlanBenefit.name };
            })

            // Filtering :
            let include = true;
            if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {

                gridConfig.filter.forEach((filter, ix) => {
                    const column = gridConfigMappedColumns.filterColumns[ix];

                    if (column) {
                        const predicate = column.filterPredicate;
                        if (predicate)
                            include = include && predicate(item, filter.value, column);
                    }
                });

            }

            if (include)
                items.push(item);
        });

        // items.sort((a, b) => { return (a.data?.name ?? '') > (b.data?.name ?? '') ? 1 : -1 });
        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)
            items.sort((a, b) => {

                let ret = 0;

                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });

        const itemsPaged = items.slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);


        return [items, itemsPaged];
    }, [plans, gridConfig]);

    const selection = useRef((() => {
        const selectedKeys: Record<string, IPlanGridItem> = {};

        const selection = new Selection({
            onSelectionChanged: () => {

                const getItems = selection.getItems() as IPlanGridItem[];
                const getSelection = selection.getSelection() as IPlanGridItem[];
                const selectedKeysArray = Object.keys(selectedKeys);

                if (getSelection.length > 0) {

                    getItems.forEach((item) => {
                        delete selectedKeys[item.key as string];
                    });


                    getSelection.forEach((item) => {
                        selectedKeys[item.key as string] = item;
                    });

                }

                const plans = Object.values(selectedKeys).map((t => t.data!));
                
                props.onChange?.(plans);
                // // if all items in current view are selected
                // if (getSelection.length === itemsPaged.length && selection.isAllSelected()) {
                //     // all current page selected
                //     items.forEach(({ key }) => {
                //         selectedKeys[key as string] = true;
                //     })
                // }
                // else if (getSelection.length === 0) {
                //     // nothing selected
                //     if (selectedKeysArray.length === items.length) {
                //         selectedKeysArray.forEach(key => delete selectedKeys[key]);
                //     }
                // }
            },

            onItemsChanged: () => {
                // need to schedule for post ui update
                setImmediate(() => {
                    const getItems = selection.getItems();

                    // do not emit change events + suppress change events
                    // https://github.dev/microsoft/fluentui/blob/master/packages/utilities/src/selection/Selection.ts
                    selection.setChangeEvents(false, true);
                    for (const { key } of getItems) {
                        if (selectedKeys[key as string])
                            selection.setKeySelected(key as string, true, false);
                        else
                            selection.setKeySelected(key as string, false, false);
                    }
                    selection.setChangeEvents(true, false)

                });
            },
            selectionMode: props.selectionMode ?? SelectionMode.none,
        });

        return selection;
    })());


    return <CustomDetailList
        setKey="key"
        columns={columns}
        items={items}
        itemsPaged={itemsPaged}
        selection={selection.current}
        selectionMode={props.selectionMode ?? SelectionMode.none}
        defaultGridConfig={gridConfig}
        quickSearchColumnKey={'planRef'}
        gridConfig={[gridConfig, setGridConfig]}
        onExtract={() => { DetailListExcelGenerationAsync(dispatch, state, application, columns, items) }}
        onShouldVirtualize={(props) => false}
        selectionPreservedOnEmptyClick={true}
        heightUnconstrained={true}
        onItemInvoked={(item: IPlanGridItem, index, event) => {
            if (item.data)
                history.push(AppLinkTo(state, { id: item.data.id, type: 'plan' }))
        }}
    />;
}
export default PlanGrid;